import { Box, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Paper, Select, TextField, Typography } from '@material-ui/core';
import { Canceler } from 'axios';
import cx from 'classnames';
import queryString from 'query-string';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ImageUpload } from '../../../components/ImageUpload';
import { SubmitControl } from '../../../components/SubmitControl';
import useBackdrop from '../../../hooks/useBackdrop';
import api from '../../../services/api/base';
import { ILink, LinkType } from '../../../services/types';
import { getPathnameFromURI } from '../../../services/utils';
import { useLink } from './reducer';
import useStyles from './styles';
import { uploadFile } from '../../../services/api/S3';
import { getLink, editLink, createLink } from '../../../services/api/Link';

interface IProps {
  linkId?: number;
  onCreated?: (data: ILink) => void;
  onError?: (error: Error) => void;
}

const DefaultImage = `featured/collection_default_banner.png`;

const WrongUrlMessage = 'The URL must start with one of the following: http:// https:// radish://';

const LinkView = (props: IProps): JSX.Element => {
  const classes = useStyles();
  const { linkId: currentLinkId, onCreated, onError } = props;

  const InputLabelProps = {
    shrink: true,
    classes: {
      root: classes.inputLabel,
    },
  };

  const InputProps = {
    classes: {
      input: classes.tfInput,
    },
  };

  const backdrop = useBackdrop();

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isUrlValid, setIsUrlValid] = useState<boolean>(true);
  const [pendingInitialLoading, setPendingInitialLoading] = useState<boolean>(false);
  const [validationMessages, setValidationMessages] = useState<string[]>([]);

  const { link, setType, setTitle, setLabel, setDescription, setUrl, setCoverUrl, initialize, getDefault } = useLink();
  const { type, title, label, desc, url, coverUrl } = link;

  const [isLightBackButton, setIsLightBackButton] = useState<boolean>(false);
  const [fileName, setFileName] = useState<string>(coverUrl || '');

  const [pendingUpload, setPendingUpload] = useState<boolean>();

  const isReadOnly = Boolean(currentLinkId) && !isEditing;

  const canceler = useRef<Canceler>(() => undefined);

  const onFileLoaded = async (file: File, blob: Blob, imageContent: string) => {
    setCoverUrl(imageContent);
    setFileName(file.name);

    try {
      setPendingUpload(true);
      const result = await uploadFile('collection_image.jpg', blob);

      if (result?.data && result?.status === 200) {
        setCoverUrl(result.data.url);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setPendingUpload(false);
    }
  };

  const onKeyDownImage = (e: React.KeyboardEvent) => {
    e.preventDefault();

    if (e.key === 'Backspace') {
      setFileName('');
      setCoverUrl('');
    }
  };

  const handleTypeChange = useCallback(
    e => {
      setType(e.target.value);
    },
    [setType],
  );

  const handleIsLightBackChange = useCallback((e, v) => {
    setIsLightBackButton(v);
  }, []);

  const fetchData = useCallback(async () => {
    if (!currentLinkId) {
      return;
    }

    canceler.current();

    try {
      setPendingInitialLoading(true);
      const [fetchPromise, cancel] = getLink(currentLinkId);
      canceler.current = cancel;
      const result = await fetchPromise;

      if (result && result.status === 200) {
        const data = result.data;

        const linkUrl = data.url;
        const { url, query } = queryString.parseUrl(linkUrl);
        setIsLightBackButton(query.text === 'light');
        delete query.text;

        data.url = queryString.stringifyUrl({
          url,
          query,
        });

        initialize(data);
        setFileName(data.coverUrl || '');
      }
    } catch (e) {
      if (api.isCancel(e)) return;
      console.error('e', e);
    } finally {
      setPendingInitialLoading(false);
    }
  }, [currentLinkId, initialize]);

  useEffect(() => {
    fetchData();

    return () => canceler.current();
  }, [fetchData]);

  useEffect(() => {
    const messageSet = new Set<string>();

    if (!title) {
      messageSet.add('A title cannot be empty.');
    }

    if (!url) {
      messageSet.add('URL cannot be empty.');
    }

    if (!coverUrl) {
      messageSet.add('A cover URL cannot be empty.');
    }

    if (!isUrlValid) {
      messageSet.add(WrongUrlMessage);
    }

    const messages = Array.from(messageSet);

    setValidationMessages(messages);
  }, [title, url, coverUrl, isUrlValid]);

  const handleUrlBlur = useCallback(() => {
    const regex = /(http|https|radish):\/\//;

    if (!regex.test(url)) {
      setIsUrlValid(false);
    }
  }, [url]);

  const handleSubmitClick = useCallback(async () => {
    try {
      backdrop.show();
      const payload: Partial<ILink> = {
        type,
        title,
        label,
        desc,
        url: `${url}${url.includes('?') ? '&' : '?'}text=${isLightBackButton ? 'light' : 'dark'}`,
        coverUrl: coverUrl ? getPathnameFromURI(coverUrl) : undefined,
      };

      let result;

      if (currentLinkId) {
        result = await editLink(currentLinkId, payload);
      } else {
        result = await createLink(payload);
      }

      if (api.isResponseSuccess(result)) {
        initialize();
        setFileName('');

        if (currentLinkId) {
          setIsEditing(false);
          fetchData();
        }

        if (onCreated) {
          onCreated(result.data as ILink);
        }
      }
    } catch (e) {
      console.error(e);
      if (onError) {
        onError(e);
      }
    } finally {
      backdrop.hide();
    }
  }, [currentLinkId, fetchData, backdrop, desc, label, title, type, url, coverUrl, initialize, isLightBackButton, onCreated, onError]);

  const handleEditClick = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleCancelClick = useCallback(() => {
    initialize();
    setFileName(coverUrl || '');

    setIsEditing(false);
  }, [initialize, coverUrl]);

  if (pendingInitialLoading) {
    return <div />;
  }

  return (
    <div className={classes.viewWrap}>
      <Box className={classes.viewTopBar}>
        <Typography variant="h6" component="h3" className={classes.viewTitle}>
          {!currentLinkId ? 'Create a Link' : <span>{getDefault().title}</span>}
        </Typography>
        <SubmitControl
          mode={!currentLinkId ? 'create' : isEditing ? 'edit' : 'view'}
          validationMessages={validationMessages}
          onSubmitClick={handleSubmitClick}
          onEditClick={handleEditClick}
          onCancelClick={handleCancelClick}
        />
      </Box>
      <Box className={classes.viewContentWrap}>
        <Paper className={classes.paneWrap}>
          <FormControl>
            <InputLabel shrink id="type-label" className={classes.inputLabel}>
              Type
            </InputLabel>

            <Select labelId="type-label" value={type} onChange={handleTypeChange} disabled={isReadOnly} className={classes.displayTypeForm}>
              <MenuItem value={LinkType.Internal}>Internal</MenuItem>
              <MenuItem value={LinkType.External}>External</MenuItem>
              <MenuItem value={LinkType.View}>View</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Title"
            className={classes.infoTextField}
            value={title}
            required
            onChange={e => {
              if (isReadOnly) return;
              setTitle(e.target.value);
            }}
            InputLabelProps={InputLabelProps}
            InputProps={InputProps}
          />
          <TextField
            label="Label"
            className={classes.infoTextField}
            value={label}
            onChange={e => {
              if (isReadOnly) return;
              setLabel(e.target.value);
            }}
            InputLabelProps={InputLabelProps}
            InputProps={InputProps}
          />
          <TextField
            label="Description"
            className={classes.infoTextField}
            value={desc}
            onChange={e => {
              if (isReadOnly) return;
              setDescription(e.target.value);
            }}
            InputLabelProps={InputLabelProps}
            InputProps={InputProps}
          />
          <TextField
            label="URL"
            className={classes.infoTextField}
            helperText={!isUrlValid ? WrongUrlMessage : undefined}
            error={!isUrlValid}
            value={url}
            onBlur={handleUrlBlur}
            onChange={e => {
              if (isReadOnly) return;
              setIsUrlValid(true);
              setUrl(e.target.value);
            }}
            InputLabelProps={InputLabelProps}
            InputProps={InputProps}
          />

          <TextField
            label="Cover URL"
            className={cx(classes.infoTextField, classes.noBottomPadding)}
            value={fileName}
            onKeyDown={onKeyDownImage}
            required
            InputLabelProps={InputLabelProps}
            InputProps={InputProps}
          />
          <FormControlLabel
            control={<Checkbox color="primary" checked={isLightBackButton} onChange={handleIsLightBackChange} />}
            label={'Shows the light color back button'}
            className={classes.formControl}
          />

          <Typography variant="subtitle1" className={classes.sectionTitle}>
            Cover
          </Typography>
          <ImageUpload onFileLoaded={onFileLoaded} src={coverUrl || DefaultImage} readonly={isReadOnly} isPending={pendingUpload} />
        </Paper>
        <Paper className={classes.paneWrap}>
          <Typography component="h5" variant="h6">
            Live
          </Typography>
          <Box className={classes.tbd}>
            <Typography variant="caption">TBD</Typography>
          </Box>
        </Paper>
      </Box>
    </div>
  );
};

export default LinkView;
